/**
 * Helper compartido para reimprimir un comprobante de caja a partir
 * del id del movimiento. Lo usan:
 *   - El feed de Caja (al hacer click en "Reimprimir" en el drawer detalle)
 *   - El historial de movimientos en la fiche del asociado
 *
 * Hace fetch del movimiento_caja completo con sus joins (asociado, serie,
 * pagos, boleta de origen, referencia bancaria de destino, parámetros
 * de inscripción si aplica) y despacha al imprimirReciboIngresos /
 * imprimirReciboEgresos correcto según la clase del movimiento.
 *
 * Devuelve {ok:true} cuando abrió la ventana del recibo, o {ok:false, error}
 * si falló algo en el fetch para que el caller pueda mostrarle al usuario.
 */
import { supabase } from './supabase'
import { imprimirReciboIngresos, imprimirReciboEgresos, type ReciboData } from './imprimibles'

type Result = { ok: true } | { ok: false; error: string }

export async function reimprimirComprobantePorMovimientoId(
  movimientoCajaId: string,
): Promise<Result> {
  // 1) Fetch del movimiento con todos los joins necesarios para el comprobante.
  // verification_code es obligatorio para el QR (mitigación anti-enumeración).
  const { data: mov, error } = await supabase
    .from('movimiento_caja')
    .select(`
      id, correlativo, fecha, hora, monto, descripcion, tipo_movimiento_codigo, verification_code,
      tipo_movimiento:tipo_movimiento_caja!inner (codigo, nombre, clase),
      serie:serie_documento_id (codigo),
      asociado:asociado_id (id, no_asociado, neo_id, nombre_completo, dpi),
      pagos:movimiento_caja_pago (
        tipo, monto, notas,
        boleta:boleta_bancaria_id (
          fecha_boleta, numero_boleta,
          cuenta:cuenta_bancaria_propia_id (banco, numero_cuenta)
        ),
        referencia_bancaria_destino:referencia_bancaria_destino_id (
          banco, numero_cuenta, titular_nombre
        )
      )
    `)
    .eq('id', movimientoCajaId)
    .single()

  if (error || !mov) {
    return { ok: false, error: error?.message ?? 'Movimiento no encontrado' }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const m = mov as any
  const isIngreso = m.tipo_movimiento?.clase === 'INGRESO'

  // 2) Cajero (email del session actual)
  const { data: sessionData } = await supabase.auth.getSession()
  const cajero = sessionData.session?.user?.email ?? 'Operador'

  // 3) Caso especial: inscripción (AIO01) → desglose con cuota + aportación
  let desglose: ReciboData['desglose']
  if (m.tipo_movimiento_codigo === 'AIO01') {
    const { data: ps } = await supabase
      .from('parametro_sistema')
      .select('clave, valor')
      .in('clave', ['aportacion.cuota_ingreso', 'aportacion.monto_inicial'])

    const params = (ps ?? []) as Array<{ clave: string; valor: string | null }>
    const cuota = Number(params.find((p) => p.clave === 'aportacion.cuota_ingreso')?.valor ?? 50)
    const aporte = Number(params.find((p) => p.clave === 'aportacion.monto_inicial')?.valor ?? 50)
    desglose = [
      { label: 'Cuota de ingreso', monto: cuota },
      { label: 'Aportación inicial', monto: aporte },
    ]
  }

  // 4) Cuenta bancaria a mostrar:
  //    - INGRESO con depósito → cuenta ORIGEN de la coop (de la boleta)
  //    - EGRESO con transferencia → cuenta DESTINO del asociado (de la referencia)
  //    - Efectivo → ninguna
  const pago = m.pagos?.[0]
  const boleta = pago?.boleta
  const refDestino = pago?.referencia_bancaria_destino
  const banco = isIngreso ? boleta?.cuenta?.banco : refDestino?.banco
  const numeroCuenta = isIngreso ? boleta?.cuenta?.numero_cuenta : refDestino?.numero_cuenta

  // 5) Concepto legible
  const concepto = m.tipo_movimiento_codigo === 'AIO01'
    ? 'Inscripción asociado'
    : (m.descripcion ?? m.tipo_movimiento?.nombre ?? '')

  // Forma de pago: si hay detalle de pago usamos su tipo real. Si NO (p.ej.
  // recibos de regularización del backfill), derivamos la forma del tipo de
  // operación en vez de caer a "efectivo" (que sería incorrecto):
  //   E13 = depósito a ahorro → depósito bancario
  //   S4  = retiro            → transferencia bancaria
  //   E10 = pago de crédito desde ahorro → débito de cuenta de ahorro
  const formaDerivada =
    m.tipo_movimiento_codigo === 'E13' ? 'deposito'
    : m.tipo_movimiento_codigo === 'S4' ? 'transferencia_bancaria'
    : m.tipo_movimiento_codigo === 'E10' ? 'debito_ahorro'
    : 'efectivo'

  const reciboData: ReciboData = {
    serie: m.serie?.codigo ?? '',
    correlativo: m.correlativo,
    fecha: `${m.fecha}T${m.hora}`,
    asociado_no: m.asociado?.no_asociado ?? '',
    neo_id: m.asociado?.neo_id,
    asociado_nombre: m.asociado?.nombre_completo ?? '',
    asociado_dpi: m.asociado?.dpi ?? '',
    concepto,
    desglose,
    monto: Number(m.monto),
    forma_pago: pago?.tipo ?? formaDerivada,
    cajero,
    banco,
    numero_cuenta: numeroCuenta,
    numero_boleta: isIngreso ? boleta?.numero_boleta : undefined,
    fecha_boleta: isIngreso ? boleta?.fecha_boleta : undefined,
    verification_code: m.verification_code,
  }

  if (isIngreso) {
    imprimirReciboIngresos(reciboData)
  } else {
    imprimirReciboEgresos(reciboData)
  }

  return { ok: true }
}
